import React, { useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, AlertTriangle, ExternalLink, Rocket,
  Copy, Check, ChevronDown, ChevronUp, Loader2,
  Volume2, Pause, Sparkles, Search, Lightbulb,
  Square,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Turn } from './types';
import { apiService } from '../../services/api';
import { useT } from '../../contexts/LocaleContext';

/**
 * DeliveryCard — v1.0 (Feb 2026).
 *
 * Premium "Cyan Neon" delivery card that closes every engine turn.
 * Enhancement over the previous SummaryCard:
 *   • Cyan-glow gradient border with subtle inner shine.
 *   • "Escuchar resumen" TTS button (uses backend default voice —
 *     configurable from Admin, remembered per-user).
 *   • Preview + Publicar primary actions unchanged.
 *   • Chat-sanitized content (no phase codes, no jargon, no cost).
 */
interface Props {
  turn: Turn;
  workspaceId?: string;
  onPreview: () => void;
  onExport: () => void;
  onShowModifiedFiles: () => void;
  onRegression: () => void;
  onRollback: () => void;
  onShowSummary: () => void;
  onDeploy?: () => void;
}

export const DeliveryCard: React.FC<Props> = ({
  turn, workspaceId, onPreview,
}) => {
  const t = useT();
  const d = turn.detail || {};
  const report = d.report || {};
  const intent = d.intent || 'analyze';
  const appReady = d.app_ready === true;
  const ok = appReady || d.satisfied !== false;

  const patches = (report.patches || []).length;
  const verifs = (report.verifications || []).filter((v: any) => v.passed).length;
  const evidence = (turn.refs?.evidence_files || []).length;
  const modifiedFiles: string[] = (report.patches || [])
    .map((p: any) => p.path).filter(Boolean);
  const autonomousIterations = d.autonomous_iterations || 0;

  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const title = useMemo(() => {
    if (!ok) return t('delivery.titleObservations');
    if (d.modification) return t('delivery.titleModApplied');
    if (intent === 'deploy') return t('delivery.titlePublished');
    if (intent === 'analyze') return t('delivery.titleAnalyzed');
    if (appReady) return t('delivery.titleAppReady');
    return t('delivery.titleDone');
  }, [ok, d.modification, intent, appReady, t]);

  const summaryText = useMemo(() => {
    const lines: string[] = [];
    lines.push(`# ${title}`);
    lines.push('');
    if (turn.content) lines.push(turn.content);
    if (d.diff_summary) lines.push(`\n${t('delivery.changes')}: ${d.diff_summary}`);
    if (d.duration_s) lines.push(`${t('delivery.time')}: ${d.duration_s}s`);
    if (patches > 0) lines.push(`${t('delivery.patches')}: ${patches}`);
    if (verifs > 0) lines.push(`${t('delivery.verifs')}: ${verifs}`);
    if (evidence > 0) lines.push(`${t('delivery.evidence')}: ${evidence}`);
    if (modifiedFiles.length > 0) {
      lines.push('');
      lines.push(`${t('delivery.modifiedFiles')}:`);
      for (const f of modifiedFiles.slice(0, 20)) lines.push(`  · ${f}`);
      if (modifiedFiles.length > 20) lines.push(`  · … ${t('delivery.andMore', { count: modifiedFiles.length - 20 })}`);
    }
    return lines.join('\n');
  }, [title, turn.content, d, patches, verifs, evidence, modifiedFiles, t]);

  // Compact narrative for TTS — never longer than ~300 chars, no lists.
  const narrativeForTts = useMemo(() => {
    const parts: string[] = [title];
    if (turn.content) parts.push(turn.content);
    if (d.diff_summary && typeof d.diff_summary === 'string' && d.diff_summary.length < 200) {
      parts.push(d.diff_summary);
    }
    // strip technical bits already handled by backend sanitizer, but
    // also trim symbols and repeated spaces for cleaner speech.
    return parts.join('. ').replace(/[·•⧉↑↓✓✕⭐]/g, '').replace(/\s+/g, ' ').trim().slice(0, 480);
  }, [title, turn.content, d.diff_summary]);

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      toast.success(t('common.copied'));
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error(t('publish.clipboardError'));
    }
  };

  const speak = async () => {
    // Último Deploy: use streaming endpoint for <1s start-up + trust
    // the browser Audio element for proper Play / Pause / Stop.
    if (speaking) {
      try { audioRef.current?.pause(); } catch { /* ignore */ }
      setSpeaking(false);
      return;
    }
    try {
      setSpeaking(true);
      // If we already generated the audio in this component, reuse it.
      if (audioRef.current && audioRef.current.currentTime > 0 && audioRef.current.currentTime < audioRef.current.duration) {
        await audioRef.current.play();
        return;
      }
      const blob = await apiService.streamTts(narrativeForTts);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeaking(false); toast.error(t('delivery.ttsError')); URL.revokeObjectURL(url); };
      await audio.play();
    } catch (err: any) {
      setSpeaking(false);
      const msg = err?.response?.data?.detail || t('delivery.ttsError');
      toast.error(msg);
    }
  };

  const stopSpeak = () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    } catch { /* ignore */ }
    setSpeaking(false);
  };

  const runOneClickPublish = async () => {
    if (!workspaceId || publishing) return;
    setPublishing(true);
    toast.loading(t('publish.inFlight'), { id: 'dc-publish' });
    try {
      const r = await apiService.publishWorkspace(workspaceId, 'auto');
      toast.dismiss('dc-publish');
      const url = r.external_url || r.url;
      toast.success(t('publish.successGeneric', { version: r.version }), {
        duration: 9000,
        description: url,
        action: {
          label: t('workspace.copyUrl'),
          onClick: () => {
            navigator.clipboard.writeText(url).catch(() => {});
            toast.success(t('publish.urlCopied'));
          },
        },
      });
    } catch (err: any) {
      toast.dismiss('dc-publish');
      const detail = err?.response?.data?.detail;
      toast.error(detail || t('publish.failed'));
    } finally {
      setPublishing(false);
    }
  };

  const showPreviewBtn = intent !== 'analyze' && !!workspaceId;
  const showPublishBtn = !!workspaceId && intent !== 'analyze' && appReady;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
      className="relative w-full max-w-[580px] rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(140deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
        border: '1px solid hsl(var(--accent) / 0.38)',
        backdropFilter: 'blur(20px) saturate(1.2)',
        boxShadow:
          '0 10px 40px -8px hsl(var(--accent) / 0.22), ' +
          'inset 0 1px 0 hsl(0 0% 100% / 0.06)',
      }}
      data-testid="chat-delivery-card"
    >
      {/* Ambient cyan glow */}
      <div
        className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: 'hsl(var(--accent) / 0.55)' }}
        aria-hidden
      />
      <div
        className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: 'hsl(var(--accent-2, var(--accent)) / 0.6)' }}
        aria-hidden
      />

      {/* Top-right action row — v1.3 minimal icons only */}
      <div className="absolute top-3 right-3 flex items-center gap-1 z-10">
        <button
          type="button"
          onClick={speak}
          disabled={!narrativeForTts}
          data-testid="chat-delivery-tts-btn"
          className="w-8 h-8 grid place-items-center rounded-full transition disabled:opacity-40"
          style={{
            background: speaking ? 'hsl(var(--accent))' : 'hsl(var(--bg) / 0.6)',
            border: `1px solid ${speaking ? 'hsl(var(--accent))' : 'hsl(var(--accent) / 0.35)'}`,
            color: speaking ? '#031014' : 'hsl(var(--accent))',
            boxShadow: speaking ? '0 4px 18px hsl(var(--accent) / 0.4)' : 'none',
          }}
          title={t('delivery.listenSummary')}
          aria-label={t('delivery.listenSummary')}
        >
          {speaking ? <Pause className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        </button>
        {speaking && (
          <button
            type="button"
            onClick={stopSpeak}
            data-testid="chat-delivery-tts-stop"
            className="w-8 h-8 grid place-items-center rounded-full transition"
            style={{
              background: 'hsl(var(--bg) / 0.6)',
              border: '1px solid hsl(var(--border))',
              color: 'hsl(var(--text-muted))',
            }}
            title="Detener"
            aria-label="Detener"
          >
            <Square className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={copySummary}
          data-testid="chat-delivery-copy-btn"
          className="w-8 h-8 grid place-items-center rounded-full transition"
          style={{
            background: 'hsl(var(--bg) / 0.6)',
            border: '1px solid hsl(var(--border))',
            color: 'hsl(var(--text-muted))',
          }}
          title={t('delivery.copySummary')}
          aria-label={t('delivery.copySummary')}
        >
          {copied ? (
            <Check className="w-3.5 h-3.5" style={{ color: 'hsl(155 65% 60%)' }} />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      <div className="relative p-5 sm:p-6">
        <div className="flex items-start gap-3.5">
          <div
            className="w-11 h-11 rounded-2xl grid place-items-center shrink-0"
            style={{
              background: ok
                ? 'linear-gradient(135deg, hsl(var(--accent) / 0.22), hsl(var(--accent) / 0.05))'
                : 'hsl(38 55% 20% / 0.6)',
              border: `1px solid ${ok ? 'hsl(var(--accent) / 0.5)' : 'hsl(38 55% 55% / 0.4)'}`,
              boxShadow: ok ? '0 4px 18px hsl(var(--accent) / 0.3)' : 'none',
            }}
          >
            {ok ? (
              <CheckCircle2 className="w-5 h-5" style={{ color: 'hsl(var(--accent))' }} />
            ) : (
              <AlertTriangle className="w-5 h-5" style={{ color: 'hsl(38 75% 70%)' }} />
            )}
          </div>

          <div className="flex-1 min-w-0 pr-24">
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-[0.28em]"
                   style={{ color: 'hsl(var(--accent))' }}>
                {t('delivery.delivered')}
              </div>
              <Sparkles className="w-3 h-3" style={{ color: 'hsl(var(--accent))' }} />
            </div>
            <div className="mt-0.5 text-[15px] font-semibold leading-tight" style={{ color: 'hsl(var(--text))' }}>
              {title}
            </div>
            <div className="mt-2 text-[13px] leading-relaxed whitespace-pre-wrap"
                 style={{ color: 'hsl(var(--text-muted))' }}
                 data-testid="chat-delivery-content">
              {turn.content}
            </div>
            {d.diff_summary && (
              <div className="mt-2 text-[11px] font-mono opacity-70" data-testid="chat-delivery-diff">
                {d.diff_summary}
              </div>
            )}

            {/* v1.1 · 3-column recap (Image 2 reference) — appears when
                the engine returns structured retrospection. */}
            {(d.implemented?.length > 0 || d.findings?.length > 0 || d.recommendations?.length > 0) && (
              <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-3 gap-4"
                   style={{ borderColor: 'hsl(var(--border))' }}
                   data-testid="chat-delivery-recap">
                {d.implemented?.length > 0 && (
                  <RecapColumn
                    icon={<Check className="w-3 h-3" />}
                    title={t('delivery.recapImplemented')}
                    items={d.implemented}
                    accent="hsl(var(--accent))"
                    testid="recap-implemented"
                  />
                )}
                {d.findings?.length > 0 && (
                  <RecapColumn
                    icon={<Search className="w-3 h-3" />}
                    title={t('delivery.recapFindings')}
                    items={d.findings}
                    accent="hsl(38 92% 60%)"
                    testid="recap-findings"
                  />
                )}
                {d.recommendations?.length > 0 && (
                  <RecapColumn
                    icon={<Lightbulb className="w-3 h-3" />}
                    title={t('delivery.recapRecommendations')}
                    items={d.recommendations}
                    accent="hsl(210 100% 65%)"
                    testid="recap-recommendations"
                  />
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest opacity-60 hover:opacity-100 transition"
              data-testid="chat-delivery-expand-toggle"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              <span>{expanded ? t('delivery.hideDetails') : t('delivery.showDetails')}</span>
            </button>
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22 }}
                  className="overflow-hidden"
                  data-testid="chat-delivery-details"
                >
                  <div className="mt-2 space-y-1 text-[11px]" style={{ color: 'hsl(var(--text-muted))' }}>
                    {d.duration_s && <div>· {t('delivery.time')}: <span className="font-mono">{d.duration_s}s</span></div>}
                    {patches > 0 && <div>· {t('delivery.patches')}: <span className="font-mono">{patches}</span></div>}
                    {verifs > 0 && <div>· {t('delivery.verifs')}: <span className="font-mono">{verifs}</span></div>}
                    {evidence > 0 && <div>· {t('delivery.evidence')}: <span className="font-mono">{evidence}</span></div>}
                    {autonomousIterations > 1 && <div>· {t('delivery.iterations')}: <span className="font-mono">{autonomousIterations}</span></div>}
                    {modifiedFiles.length > 0 && (
                      <div className="pt-1">
                        <div className="opacity-70 mb-1">{t('delivery.modifiedFiles')}:</div>
                        <ul className="pl-3 space-y-0.5">
                          {modifiedFiles.slice(0, 8).map(f => (
                            <li key={f} className="font-mono text-[10px] opacity-80 truncate">· {f}</li>
                          ))}
                          {modifiedFiles.length > 8 && (
                            <li className="text-[10px] opacity-50">… {t('delivery.andMore', { count: modifiedFiles.length - 8 })}</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {(showPreviewBtn || showPublishBtn) && (
              <div className="mt-4 flex flex-wrap gap-2">
                {showPreviewBtn && (
                  <button
                    type="button"
                    onClick={onPreview}
                    data-testid="chat-delivery-preview-btn"
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition"
                    style={{
                      background: 'hsl(var(--bg) / 0.7)',
                      border: '1px solid hsl(var(--border))',
                      color: 'hsl(var(--text))',
                    }}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>{t('workspace.preview')}</span>
                  </button>
                )}
                {showPublishBtn && (
                  <button
                    type="button"
                    onClick={runOneClickPublish}
                    disabled={publishing}
                    data-testid="chat-delivery-deploy-btn"
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition disabled:opacity-60"
                    style={{
                      background: 'hsl(var(--accent))',
                      border: '1px solid hsl(var(--accent))',
                      color: '#031014',
                      boxShadow: '0 4px 18px hsl(var(--accent) / 0.4)',
                    }}
                  >
                    {publishing ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Rocket className="w-3.5 h-3.5" />
                    )}
                    <span>{publishing ? t('workspace.publishing') : t('publish.button')}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

function _b64ToBlob(_b64: string, _contentType: string): Blob {
  // Retained for backwards compatibility only — the streaming path
  // returns a Blob directly. Remove once no callers depend on it.
  return new Blob();
}

const RecapColumn: React.FC<{
  icon: React.ReactNode;
  title: string;
  items: string[];
  accent: string;
  testid: string;
}> = ({ icon, title, items, accent, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest font-medium"
         style={{ color: accent }}>
      {icon}<span>{title}</span>
    </div>
    <ul className="space-y-1.5 text-[12px]" style={{ color: 'hsl(var(--text-muted))' }}>
      {items.slice(0, 8).map((item, i) => (
        <li key={i} className="flex items-start gap-1.5 leading-snug">
          <span className="mt-1 w-1 h-1 rounded-full shrink-0" style={{ background: accent }} />
          <span>{item}</span>
        </li>
      ))}
      {items.length > 8 && (
        <li className="text-[10px] opacity-50">+ {items.length - 8} más</li>
      )}
    </ul>
  </div>
);
